/**
 * http module
 * AFTER UPDATE AXIOS to >= v0.22.0, change cancelToken to abortController API
 */

import axios from 'axios';
import { session } from './vue/utlis/session';

/**
 * http axios instance with auto canceling requests interceptor
 *
 * added `config.key` axios property: - if passed, then only last request will be completed (with equals keys)
 * added `config.includeSessionId` axios property: - if passed, then sessid query parameter will be added to request
 *
 * added `error.isCancelled` flag in axios error callback object - if is `true` - request was been cancelled
 * added `error.retry` function could be called for repeat request
 */
export class Http {
	constructor(config) {
		this.httpAbortControllers = new Map();
		const http = axios.create(config);
		Object.assign(this, http);

		this.interceptors.request.use(
			(config) => {
				const key = config.key ?? Symbol();

				if (this.httpAbortControllers.has(key)) {
					// cancel previous request
					this.httpAbortControllers.get(key).cancel();
				}
				// set new cancel source
				this.httpAbortControllers.set(key, axios.CancelToken.source());

				config.cancelToken = this.httpAbortControllers.get(key).token;

				if (config.includeSessionId) {
					if (!config.params) config.params = {};
					config.params.sessid = session.sessid;
				}

				return config;
			},
			(error) => {
				return Promise.reject(error);
			}
		);

		this.interceptors.response.use(
			(response) => {
				// when request is finished, remove old cancel source
				this.httpAbortControllers.delete(response.config.key);

				return response;
			},
			(error) => {
				if (axios.isCancel(error)) {
					error.isCancelled = true;
				} else {
					// when request is broken, remove old cancel source
					this.httpAbortControllers.delete(error.config?.key);
				}

				const errorWithRetry = {
					...error,
					retry: () => this.request(error.config),
				}

				return Promise.reject(errorWithRetry);
			}
		);

	}
}

export const http = new Http();
